import { motion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
    Star, Package, ShieldCheck, Heart, Lock
} from '@phosphor-icons/react';

export function ShopItemCard({ item, isListing = false, tier, verifiedSellers, wishlistIds, onItemClick, onToggleWishlist }) {
    const isVerified = verifiedSellers.some(
        s => s.display_name?.toLowerCase() === item.brand?.toLowerCase()
    );
    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -4 }}
            className="cursor-pointer" onClick={() => onItemClick(item, isListing)}>
            <Card className="glass-card overflow-hidden h-full">
                <div className="aspect-square relative bg-muted">
                    {item.image_url ? (
                        <img src={item.image_url} alt={item.name || item.title} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <Package size={48} className="text-muted-foreground" />
                        </div>
                    )}
                    {item.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground">
                            <Star size={12} className="mr-1" weight="fill" />Featured
                        </Badge>
                    )}
                    {isListing && <Badge className="absolute top-2 right-2 bg-blue-500 text-white">P2P</Badge>}
                    {isVerified && !isListing && (
                        <Badge className="absolute bottom-2 left-2 bg-neon-green/90 text-black text-[10px]" data-testid="seller-verified-badge">
                            <ShieldCheck size={10} className="mr-0.5" weight="fill" />Verified
                        </Badge>
                    )}
                    {!isListing && item.item_id && (
                        <button onClick={(e) => onToggleWishlist(e, item.item_id)}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-transform hover:scale-110 z-10"
                            data-testid={`wishlist-${item.item_id}`}>
                            <Heart size={16} weight={wishlistIds.has(item.item_id) ? 'fill' : 'regular'}
                                className={wishlistIds.has(item.item_id) ? 'text-plasma-red' : 'text-white'} />
                        </button>
                    )}
                    {tier === 'free' && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                            <Lock size={24} className="text-white" />
                        </div>
                    )}
                </div>
                <CardContent className="p-3">
                    <h3 className="font-medium text-sm truncate text-foreground">{item.name || item.title}</h3>
                    {item.brand && <p className="text-xs text-muted-foreground">{item.brand}</p>}
                    {item.avg_rating > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                            <Star size={10} weight="fill" className="text-neon-gold" />
                            <span className="text-[10px] text-muted-foreground">{item.avg_rating} ({item.review_count})</span>
                        </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between">
                        <span className="font-mono font-bold text-neon-gold text-sm">{item.price_boltz?.toLocaleString()} Boltz</span>
                        {item.stock === 0 ? (
                            <Badge className="bg-plasma-red/10 text-plasma-red border-plasma-red/20 text-[10px]" data-testid="sold-out-badge">Sold Out</Badge>
                        ) : (
                            <span className="text-xs text-muted-foreground font-mono">~{((item.price_boltz || item.price_rmr || 0) / 1000).toFixed(4)} SOL</span>
                        )}
                    </div>
                    <div className="mt-2">
                        {tier === 'free' ? (
                            <span className="text-[10px] text-muted-foreground/60 font-display">Browse Only</span>
                        ) : (tier === 'standard' || tier === 'vip') ? (
                            <span className="text-[10px] text-primary font-display">Buy</span>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}
